import { clone } from 'lodash';
import ReactDomServer from 'react-dom/server';

import moment from 'moment';
import { AppConstants } from '../../../constants/AppConstants';
import { createSignedURL, uploadFile } from '../../../http/asset-management-services';
import { HTTP_STATUS } from '../../../http/constants/http.status';

import {
  createSurveyTemplate,
  getQuestionnaire,
  updateSurveyTemplate
} from '../../../http/survey-service';
import { getGlobalBucketName } from '../../../util/AppSetting';

export const appendDateTimeStamp = (name: string) => {
  return `${name}_copy_${moment().format('YYYYMMDDHHMMSS')}`;
};

export const surveyCopy = async (data: any, surveyName: any) => {
  const { id: surveyId } = data;
  const surveyDetailsPayload = createPayLoadForSurveyDetails(data, surveyName);
  const surveyQuestionResponse = await getSurveyQuestions(surveyId);
  const { item: surveyQuestions } = surveyQuestionResponse;
  const createSurveyResponse = await createCopySurvey(surveyDetailsPayload, surveyQuestions);
  return createSurveyResponse;
};

export const createCopySurvey = async (surveyDetails: any, surveyQuestions: any) => {
  const createSurveyDetials = await createSurveyTemplate(surveyDetails);
  if (
    createSurveyDetials &&
    [HTTP_STATUS.HTTP_CREATED, HTTP_STATUS.HTTP_OK].includes(createSurveyDetials.status)
  ) {
    const { id: surveyId } = createSurveyDetials.data;
    const surveyQuestionsPayLoad = await createPayLoadForQuestions(surveyQuestions, surveyId);
    if (surveyQuestionsPayLoad) {
      const updateSurveyPayload = { ...surveyDetails, item: surveyQuestionsPayLoad };
      let response = await updateSurveyTemplate(surveyId, updateSurveyPayload);
      if ([HTTP_STATUS.HTTP_CREATED, HTTP_STATUS.HTTP_OK].includes(response.status)) {
        //return surveyId;
        return response;
      } else {
        return response;
      }
    } else {
      return false;
    }
  } else {
    return createSurveyDetials;
  }
};

export const getSurveyQuestions = async (id: string) => {
  const response = await getQuestionnaire(id);
  return response.data;
};

export const createPayLoadForSurveyDetails = (data: any, surveyName: any) => {
  const surveyDetails = clone(data);
  const { name } = surveyDetails;
  return {
    ...surveyDetails,
    //name: appendDateTimeStamp(name),
    name: surveyName,
    status: 'draft',
    effectivePeriodStart: null,
    effectivePeriodEnd: null
  };
};

const getBodyMapImages = (extension: any) => {
  const imagesArr = [];
  const { bodyMapImageLabel, path, bucketName } = extension;
  const clonedBodyMapImageLabel = clone(bodyMapImageLabel);
  for (let k in clonedBodyMapImageLabel) {
    imagesArr.push({
      bucketName,
      path,
      imageName: clonedBodyMapImageLabel[k].imageName
    });
  }
  return imagesArr;
};

const getLikertImages = (extension: any) => {
  const imagesArr = [];
  const { likertImages, path, bucketName } = extension;
  for (let k in likertImages) {
    imagesArr.push({
      bucketName,
      path,
      imageName: likertImages[k]
    });
  }
  return imagesArr;
};

const getMediaFiles = (extension: any) => {
  const { fileName, path, bucketName } = extension;
  const imagesArr = [
    {
      bucketName,
      path,
      imageName: fileName
    }
  ];
  return imagesArr;
};

const getFile = async (imagesList: any) => {
  const downloadedImagesList = [];
  for (const k in imagesList) {
    const { url, fileName } = imagesList[k];
    const fileContent = await fetch(url);
    let fileData = await fileContent.blob();
    var newImageFIle = new File([fileData], fileName, { type: fileData.type });
    downloadedImagesList.push({
      ...imagesList[k],
      file: [newImageFIle]
    });
  }
  return downloadedImagesList;
};

const downLoadSurveyImages = async (imagesArr: any) => {
  if (!imagesArr && !imagesArr.length) {
    return;
  }
  let createSignedURLArr: any = [];
  let downloadFileArr: any = [];

  Object.values(imagesArr).forEach((image: any) => {
    const { path, imageName, bucketName } = image;
    if (imageName) {
      const payload = {
        filePath: `${path}${imageName}`,
        fileName: imageName,
        bucketName: bucketName,
        versioning: true,
        expirationTime: 1440,
        action: AppConstants.GCS_UPLOAD_DOWNLOAD,
        publicObject: true
      };
      createSignedURLArr.push(createSignedURL(payload));
    }
  });

  if (createSignedURLArr.length) {
    await Promise.all(createSignedURLArr).then((signedDataArr: any) => {
      signedDataArr.forEach((signedData: any, index: any) => {
        if (signedData.status === HTTP_STATUS.HTTP_CREATED) {
          let { url, headers = {} } = signedData.data;
          let { data: fileData } = signedData?.config;
          downloadFileArr.push({
            url: url,
            fileName: JSON.parse(fileData).fileName
          });
        }
      });
    });
  }
  return downloadFileArr;
};

const uploadSurveyImages = async (imagesArr: any, surveyPath: any) => {
  let createSignedURLArr: any = [];
  let uploadFileArr: any = [];
  let uplodedFileObjArray: any = [];

  for (let k in imagesArr) {
    const { file, fileName } = imagesArr[k];
    if (file) {
      uplodedFileObjArray.push(imagesArr[k]);
      const [fileObj] = file;
      if (fileObj.name) {
        const payload = {
          filePath: `${surveyPath}${fileName}`,
          bucketName: getGlobalBucketName(),
          versioning: true,
          action: AppConstants.GCS_UPLOAD_ACTION,
          publicObject: true,
          expirationTime: AppConstants.GCS_MAX_EXPIRATION_TIME,
          metadata: {
            fileName: fileObj.name,
            type: fileObj.type,
            size: fileObj.size,
            [AppConstants.HEADER_CACHE_CONTROL]: AppConstants.VALUE_NO_CACHE,
            [AppConstants.HEADER_MAX_AGE]: AppConstants.VALUE_MAX_AGE_MINIMUM,
            [AppConstants.CONTENT_DISPOSITION]: `attachment; filename=${fileName}`
          }
        };

        createSignedURLArr.push(createSignedURL(payload));
      }
    }
  }

  if (createSignedURLArr.length) {
    await Promise.all(createSignedURLArr).then((signedDataArr: any) => {
      signedDataArr.forEach((signedData: any, index: any) => {
        if (signedData.status === HTTP_STATUS.HTTP_CREATED) {
          let { url, headers = {} } = signedData.data;
          if (uplodedFileObjArray[index].file) {
            const [fileObj] = uplodedFileObjArray[index].file;
            const uploadPayload = {
              url,
              body: fileObj,
              headers: { ...headers, [AppConstants.HEADER_CONTENT_TYPE]: fileObj.type }
            };

            uploadFileArr.push(
              uploadFile(
                uploadPayload,
                () => {},
                (err: string) => {
                  return false;
                }
              )
            );
          }
        } else {
          return false;
        }
      });
    });
    if (uploadFileArr.length) {
      await Promise.all(uploadFileArr);
    }
  }
  return true;
};

export const updateSurveyQuestionPath = (item: any, surveyId: any, imageArrObjToDownload: any) => {
  const { displayType, extension = {} } = item;
  if (!extension) {
    return {};
  }
  const { ratingScale = '' } = extension;
  let extensionObj: any;
  const surveyImagesPath = `${AppConstants.SURVEY_MEDIA_PATH}${surveyId}/`;

  //if(item.type === 'group')

  switch (displayType) {
    case 'bodyMapSingle':
    case 'bodyMapMulti':
    case 'bodymap':
      imageArrObjToDownload.push(...getBodyMapImages(extension));
      extensionObj = { ...extension, path: surveyImagesPath };
      break;
    case 'likertIconsWithLabels':
    case 'likertIcons':
      if (ratingScale && ratingScale === 'Custom Range') {
        imageArrObjToDownload.push(...getLikertImages(extension));
        extensionObj = { ...extension, path: surveyImagesPath };
      } else {
        extensionObj = { ...extension };
      }
      break;
    case 'media':
      imageArrObjToDownload.push(...getMediaFiles(extension));
      extensionObj = { ...extension, path: surveyImagesPath };
      break;
    default:
      extensionObj = {
        ...extension
      };
  }

  return extensionObj;
};

export const getQuestionsArrObj = (questions: any, surveyId: any, imageArrObjToDownload: any) => {
  const updatedQueData: any = [];

  questions.forEach((item: any) => {
    const updatedQueItem: any = { ...item };
    delete updatedQueItem.id;
    delete updatedQueItem.questionnaireId;
    updatedQueItem.extension = updateSurveyQuestionPath(item, surveyId, imageArrObjToDownload);
    updatedQueItem.item =
      updatedQueItem.type === 'group'
        ? getQuestionsArrObj(updatedQueItem.item, surveyId, imageArrObjToDownload)
        : [];
    updatedQueData.push(updatedQueItem);
  });

  return updatedQueData;
};

export const createPayLoadForQuestions = async (questions: any, surveyId: any) => {
  if (!questions || !questions.length) {
    return [];
  }
  const surveyImagesPath = `${AppConstants.SURVEY_MEDIA_PATH}${surveyId}/`;
  const imageArrObjToDownload: any = [];
  const questionData = getQuestionsArrObj(questions, surveyId, imageArrObjToDownload);
  // const questionsPayload = questions.map((item: any) => {
  //   delete item.id;
  //   delete item.questionnaireId;
  //   const updatedQueItem: any = {};

  //   return updateSurveyQuestionPath(item, surveyId, imageArrObjToDownload);
  // });
  const downLoadedImagesList = await downLoadSurveyImages(imageArrObjToDownload);
  const downLoadedImages = await getFile(downLoadedImagesList);
  const uploadImages = await uploadSurveyImages(downLoadedImages, surveyImagesPath);
  return uploadImages ? questionData : false;
};
